const assumptions = {
  accountOpeningDuration: 8,
  transferDuration: 14,
  accountOpenDay: 3,
  chargingWeek: 2,
  chargingDay: 5,
  chargingFee: 0.0145 / 12,
  paidPercentage: 1 / 1.45,
  bonus: 0.02 / 36,
};

const addBusinessDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  let addedDays = 0;
  while (addedDays < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) {
      addedDays++;
    }
  }
  return result;
};

const getStartDayOfBonus = (year: number, monthIndex: number): string => {
  let prevMonth = monthIndex - 1;
  let prevYear = year;
  if (prevMonth < 0) {
    prevMonth = 11;
    prevYear -= 1;
  }

  const previousMonthBonusEnd = getEndDayOfBonus(prevYear, prevMonth);

  const bonusStart = new Date(previousMonthBonusEnd);
  bonusStart.setDate(bonusStart.getDate() + 1);

  return bonusStart.toISOString().split("T")[0];
};

const getEndDayOfBonus = (year: number, monthIndex: number): string => {
  const lastThursday = new Date(year, monthIndex + 1, 0);
  while (lastThursday.getDay() !== 4) {
    lastThursday.setDate(lastThursday.getDate() - 1);
  }

  const lastDayOfMonth = new Date(year, monthIndex + 1, 0).getDate();
  if (lastThursday.getDate() === lastDayOfMonth) {
    lastThursday.setDate(lastThursday.getDate() - 7);
  }

  return lastThursday.toISOString().split("T")[0];
};

export const calculatePlotData = (
  startDate: Date,
  weeksData: { numberOfClients: number; accountValue: number; week: number }[]
) => {
  const feeMap: Record<
    string,
    {
      cumulativeTotal: number;
      fee: number;
      Payment: number;
      cumulativeBonus: number;
      delayBonus: number;
      Bonus: number;
    }
  > = {};

  const monthlyPayments: { month: string; Payment: number; Bonus: number }[] =
    [];
  const detail: Record<
    string,
    { month: string; paymentAmount: number; bonus: number }
  > = {};

  const dailyDeposits: Record<string, number> = {};

  const startMonthLabel = startDate.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });

  feeMap[startMonthLabel] = {
    cumulativeTotal: 0,
    fee: 0,
    Payment: 0,
    cumulativeBonus: 0,
    delayBonus: 0,
    Bonus: 0,
  };

  let partialDaysThisWeek = 5;
  const dayOfWeek = startDate.getDay();
  if (dayOfWeek !== 1) {
    partialDaysThisWeek = 6 - dayOfWeek;
  }
  const partialWeekEnd = addBusinessDays(startDate, partialDaysThisWeek - 1);
  const firstFullWeekMonday = new Date(partialWeekEnd);
  while (firstFullWeekMonday.getDay() !== 1) {
    firstFullWeekMonday.setDate(firstFullWeekMonday.getDate() + 1);
  }

  weeksData.forEach(({ accountValue, week }) => {
    const isFirstWeek = week === 1;
    const daysThisWeek = isFirstWeek ? partialDaysThisWeek : 5;

    const weekStartDate = isFirstWeek
      ? startDate
      : addBusinessDays(firstFullWeekMonday, (week - 2) * 5);

    const accountOpenDate = addBusinessDays(
      weekStartDate,
      assumptions.accountOpeningDuration
    );
    const moneyTransferDate = addBusinessDays(
      accountOpenDate,
      assumptions.transferDuration
    );

    console.log(
      "startDate",
      startDate,
      "accountOpenDate",
      accountOpenDate,
      "moneyTransferDate",
      moneyTransferDate
    );

    const dailyAmount = accountValue / daysThisWeek;
    for (let d = 0; d < daysThisWeek; d++) {
      const transferDay = addBusinessDays(moneyTransferDate, d);
      const dataKey = transferDay.toISOString().split("T")[0];
      dailyDeposits[dataKey] = (dailyDeposits[dataKey] || 0) + dailyAmount;

      const monthLabel = transferDay.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });

      if (!feeMap[monthLabel]) {
        feeMap[monthLabel] = {
          cumulativeTotal: 0,
          fee: 0,
          Payment: 0,
          cumulativeBonus: 0,
          delayBonus: 0,
          Bonus: 0,
        };
      }
      feeMap[monthLabel].cumulativeTotal += dailyAmount;
    }
  });

  let sortedMonths = Object.keys(feeMap).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  if (Object.keys(dailyDeposits).length !== 0) {
    const latestDepositDate = new Date(
      Math.max(...Object.keys(dailyDeposits).map((d) => new Date(d).getTime()))
    );

    let lastBonusMonth = new Date(latestDepositDate);

    const lastBonusEndDay = getEndDayOfBonus(
      lastBonusMonth.getFullYear(),
      lastBonusMonth.getMonth()
    );

    if (latestDepositDate.toISOString().split("T")[0] > lastBonusEndDay) {
      lastBonusMonth.setMonth(lastBonusMonth.getMonth() + 1);
    }

    let currentMonth = new Date(`1 ${sortedMonths[0]}`);

    while (
      currentMonth.getFullYear() < lastBonusMonth.getFullYear() ||
      (currentMonth.getFullYear() === lastBonusMonth.getFullYear() &&
        currentMonth.getMonth() <= lastBonusMonth.getMonth())
    ) {
      const monthLabel = currentMonth.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });

      if (!feeMap[monthLabel]) {
        feeMap[monthLabel] = {
          cumulativeTotal: 0,
          fee: 0,
          Payment: 0,
          cumulativeBonus: 0,
          delayBonus: 0,
          Bonus: 0,
        };
      }

      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    sortedMonths = Object.keys(feeMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    // Calculate cumulative bonus
    for (let i = 0; i < sortedMonths.length; i++) {
      const currMonth = sortedMonths[i];
      const prevMonth = sortedMonths[i - 1];

      const currMonthDateObj = new Date(`5 ${currMonth} 00:00:00 GMT`);

      const formattedCurrMonth = currMonthDateObj.toISOString().split("T")[0];

      const bonusStartDay = getStartDayOfBonus(
        new Date(formattedCurrMonth).getFullYear(),
        new Date(formattedCurrMonth).getMonth()
      );

      const bonusEndDay = getEndDayOfBonus(
        new Date(formattedCurrMonth).getFullYear(),
        new Date(formattedCurrMonth).getMonth()
      );

      Object.keys(dailyDeposits).forEach((day) => {
        if (
          new Date(day) >= new Date(bonusStartDay) &&
          new Date(day) <= new Date(bonusEndDay)
        ) {
          feeMap[currMonth].cumulativeBonus += dailyDeposits[day];
        }
      });

      if (i > 0) {
        feeMap[currMonth].cumulativeTotal += feeMap[prevMonth].cumulativeTotal;
        feeMap[currMonth].cumulativeBonus += feeMap[prevMonth].cumulativeBonus;
      }
    }

    //calculate fee, payment and bonus
    for (let i = 0; i < sortedMonths.length; i++) {
      if (i === 0) {
        feeMap[sortedMonths[i]].fee = 0;
        feeMap[sortedMonths[i]].Payment = 0;
        feeMap[sortedMonths[i]].Bonus = 0;
      } else {
        const prev = sortedMonths[i - 1];
        const curr = sortedMonths[i];
        feeMap[curr].fee =
          feeMap[prev].cumulativeTotal * assumptions.chargingFee;
        feeMap[curr].Payment =
          Math.round(feeMap[prev].fee * assumptions.paidPercentage * 100) / 100;
        feeMap[curr].delayBonus =
          Math.round(feeMap[prev].cumulativeBonus * assumptions.bonus * 100) /
          100;
        feeMap[curr].Bonus = feeMap[prev].delayBonus;
      }
    }

    let keepGenerating = true;
    let iterationCount = 0;
    const MAX_ITERATIONS = 5;

    while (keepGenerating && iterationCount < MAX_ITERATIONS) {
      iterationCount++;
      const prevMonthLabel = sortedMonths[sortedMonths.length - 1];

      const [pMonthStr, pYearStr] = prevMonthLabel.split(" ");
      const pMonthIndex = new Date(`${pMonthStr} 1, ${pYearStr}`).getMonth();
      const pYearIndex = new Date(`${pMonthStr} 1, ${pYearStr}`).getFullYear();

      const nextMonthDate = new Date(pYearIndex, pMonthIndex + 1, 1);
      const nextMonthLabel = nextMonthDate.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });

      if (feeMap[nextMonthLabel]) {
        break;
      }

      feeMap[nextMonthLabel] = {
        cumulativeTotal: feeMap[prevMonthLabel].cumulativeTotal,
        fee: 0,
        Payment: 0,
        cumulativeBonus: feeMap[prevMonthLabel].cumulativeBonus,
        delayBonus: 0,
        Bonus: 0,
      };

      sortedMonths.push(nextMonthLabel);

      feeMap[nextMonthLabel].fee =
        feeMap[prevMonthLabel].cumulativeTotal * assumptions.chargingFee;
      feeMap[nextMonthLabel].Payment =
        Math.round(
          feeMap[prevMonthLabel].fee * assumptions.paidPercentage * 100
        ) / 100;
      feeMap[nextMonthLabel].delayBonus =
        Math.round(
          feeMap[prevMonthLabel].cumulativeBonus * assumptions.bonus * 100
        ) / 100;
      feeMap[nextMonthLabel].Bonus = feeMap[prevMonthLabel].delayBonus;

      if (
        feeMap[nextMonthLabel].Payment === feeMap[prevMonthLabel].Payment &&
        feeMap[nextMonthLabel].Bonus === feeMap[prevMonthLabel].Bonus
      ) {
        keepGenerating = false;
      }
    }

    sortedMonths.forEach((monthLabel) => {
      const { Payment, Bonus } = feeMap[monthLabel];
      monthlyPayments.push({ month: monthLabel, Payment, Bonus });
      detail[`payment${Object.keys(detail).length + 1}`] = {
        month: monthLabel,
        paymentAmount: Payment,
        bonus: Bonus,
      };
    });
  }

  const depositDates = Object.keys(dailyDeposits).sort();

  const firstDate = depositDates[0];
  const lastDate = depositDates[depositDates.length - 1];

  return { monthlyPayments, detail, firstDate, lastDate };
};
